/**
 * Client-side chain token creation, verification, storage, and IndexedDB wallet.
 *
 * Creates anchor-model delegations (a self-signed root ANCHOR — inert
 * structure, published at /auth/anchors/{hash} — plus a time-boxed LEAF, the
 * presentable credential, stored at /auth/chains/{hash}) and keeps them
 * locally in IndexedDB as a "capability wallet" with metadata, keyed by
 * anchor.
 *
 * Also provides full chain verification: resolve the anchor chain by fetching
 * anchors from the server, verify each link's signature against the signer's
 * public key, enforce scope attenuation, depth limits, and revocation
 * checking, then verify the leaf against its anchor. Legacy parent-linked
 * chains are refused (the dual-accept window closed 2026-10-06).
 */

import {
    acequiaReady,
    getUserId,
    getDeviceId,
    getUserKeys,
} from '../acequia/acequia.js'
import { jose, localforage } from '../deps.js'
import { isBrowser } from '../acequia/utils.js'
import { ownIdentityFor } from './identities.js'
import { watchStore } from '../acequia/storageWatchdog.js'

// Content-addressed cache of chain links (anchors), keyed by hash.
//
// Deliberately separate from the wallet: a wallet row means "a grant I hold"
// and carries a name, scope and status that show up in the token UI. This
// holds bytes someone asked for by hash, nothing more. Deleting a wallet row
// is a decision; deleting a cache entry costs one refetch.
//
// Anchors are immutable by construction, so there is no invalidation, no
// freshness rule and no staleness window — the failure mode that shut caching
// down on this platform cannot occur for a name that *is* the hash of its
// content.
let linkStore

// Refuse to cache anything absurd. Anchors run ~850 bytes; this is only here
// so a malformed or hostile response cannot fill storage.
const MAX_CACHED_LINK_BYTES = 64 * 1024

// Legacy parent-linked chain credentials are REMOVED (2026-10-06,
// close-the-dual-accept-window) — the migration-pointing refusal, mirroring
// the server's src/auth/legacyChainAuth.mjs.
export const LEGACY_CHAIN_MESSAGE =
    'Legacy parent-linked chain tokens are no longer accepted (the dual-accept ' +
    'window closed 2026-10-06). Delegations are anchor-model: redeem your short ' +
    'code (?token=<code>) for a current leaf, or ask the grantor to mint a new ' +
    'delegation.'

// IndexedDB wallet store — separate from the main acequia store
let walletStore
// The public keys of this account's devices, kept so a grant signed on one of
// them verifies on another with the auth server unreachable (unified-token-
// system, decision 8, answer 4). kid → { jwk, userId, status, cachedAt }.
let accountKeyStore
if (typeof window !== 'undefined' || typeof self !== 'undefined') {
    // Browser or Service Worker
    try {
        walletStore = watchStore(localforage.createInstance({ name: 'acequia-chains' }), 'acequia-chains')
        linkStore = watchStore(localforage.createInstance({ name: 'acequia-chain-links' }), 'acequia-chain-links')
        accountKeyStore = watchStore(localforage.createInstance({ name: 'acequia-account-keys' }), 'acequia-account-keys')
    } catch {
        // Fallback: will be null in non-browser contexts
    }
}

/**
 * Browser-compatible SHA-256 hex hash of a JWT string.
 * Matches the server's hashToken() in src/auth/utils.mjs.
 * @param {string} jwtString - Raw JWT string
 * @returns {Promise<string>} Hex-encoded SHA-256 hash
 */
export async function hashToken(jwtString) {
    const data = new TextEncoder().encode(jwtString)
    const buf = await crypto.subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * Is this token an anchor presented as a credential? Anchors are inert
 * structural nodes — public and fetchable by design — so they must never
 * authenticate. The governing invariant (publicly fetchable ⇒ not
 * presentable) holds at EVERY verifier, and the browser library is a
 * verifier: BrowserDAV and peers verify presented tokens with it. Mirrors
 * the server's src/auth/anchorNotPresentable.mjs. Self-describing (the
 * typ:"anchor" protected header), no store lookup.
 */
export function isPresentedAnchor(token) {
    if (typeof token !== 'string') return false
    try {
        return jose.decodeProtectedHeader(token).typ === 'anchor'
    } catch {
        return false
    }
}

/**
 * Create a root JWT for chain delegation.
 * No parent claim, no scope — just identifies the user and sets expiry.
 * This root anchors the chain and is stored on the server for resolution.
 *
 * @param {number} expiresInSeconds - Seconds from now until expiry
 * @returns {Promise<string>} Signed root JWT
 */
export async function createRootToken(expiresInSeconds) {
    await acequiaReady()

    const userId = getUserId()
    const deviceId = getDeviceId()
    const userKeys = getUserKeys()

    if (!userKeys) {
        throw new Error('User keys not available — register or link a device first')
    }

    const userPublicJWK = await jose.exportJWK(userKeys.publicKey)
    const kid = await jose.calculateJwkThumbprint(userPublicJWK)

    const now = Math.floor(Date.now() / 1000)

    const token = new jose.SignJWT({
        kid,
        deviceId,
    })
        .setSubject(userId)
        .setIssuedAt()
        .setProtectedHeader({ alg: 'PS256', kid })

    if (expiresInSeconds) {
        token.setExpirationTime(now + expiresInSeconds)
    }

    return await token.sign(userKeys.privateKey)
}

/**
 * Scope as it should be embedded in a minted token: always the canonical
 * grant set. Legacy { paths, writePaths } input is still accepted and
 * expanded (paths → read, writePaths → the four write privileges), exactly
 * as every verifier's canonicalizeScope already reads it, so the signed
 * triples mean what the legacy shape meant. Tokens carry triples, never
 * names or the two-verb shape (unified-token-system decision 6, Phase 1
 * step 1). This is the single place client mint functions normalize scope;
 * callers need not change what they pass.
 */
function scopeForPayload(scope) {
    return canonicalizeScope(scope)
}

// createScopedToken (the legacy `parent`-linked child mint) was removed with
// the dual-accept window (2026-10-06, close-the-dual-accept-window). Minting
// is anchor-model: createRootAnchor / createLeafForAnchor / createChainToken.

/**
 * Store a JWT on the server at its content-addressable URL.
 * PUT /auth/chains/{hash} with raw JWT body.
 *
 * @param {string} authUrl - Base auth URL (e.g., '/auth')
 * @param {string} authToken - Bearer token for authentication
 * @param {string} jwt - Raw JWT string to store
 * @returns {Promise<{ hash: string, id: string }>} The SHA-256 hash and CUID2 short ID
 */
/**
 * Headers for an authenticated store request.
 *
 * Only sends `Authorization` when there is a token to send. A same-origin page
 * usually has none — it is authenticated by the `auth_token` cookie — and
 * `Bearer ` with nothing after it is not "no credential", it is a header that
 * reads as bearer auth while the cookie does the work. The server happened to
 * tolerate it (an empty token falls through to the cookie); the point of
 * omitting it is that the request now says how it is authenticated.
 */
function storeHeaders(authToken, extra = {}) {
    return authToken ? { 'Authorization': `Bearer ${authToken}`, ...extra } : { ...extra }
}

export async function storeChainToken(authUrl, authToken, jwt, { reuseId } = {}) {
    const hash = await hashToken(jwt)

    const url = reuseId
        ? `${authUrl}/chains/${hash}?reuseId=${encodeURIComponent(reuseId)}`
        : `${authUrl}/chains/${hash}`

    const resp = await fetch(url, {
        method: 'PUT',
        headers: storeHeaders(authToken, { 'Content-Type': 'text/plain' }),
        body: jwt,
    })

    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.message || `Failed to store chain token: ${resp.status}`)
    }

    const data = await resp.json()
    return { hash, id: data.id }
}

/**
 * Full workflow: create an anchor-model delegation (self-signed root anchor +
 * time-boxed leaf) and store it on server + IndexedDB wallet.
 *
 * @param {Object} options
 * @param {string} options.authUrl - Base auth URL (e.g., '/auth')
 * @param {string} options.authToken - Bearer token for authentication
 * @param {string} options.name - Display name for the token
 * @param {string} [options.note] - Optional description
 * @param {{ paths: string[], writePaths: string[] }} options.scope - Permission scope
 * @param {number} options.expiresInSeconds - Seconds from now until expiry
 * @returns {Promise<{ rootHash: string, leafHash: string, leafJwt: string, leafId: string }>}
 */
/**
 * Create a self-signed ROOT ANCHOR (anchor model). Inert structural node:
 * typ:"anchor" header, the user's own key, no working `exp`, no short code.
 * Durable labels (name/note) and audit provenance ride on the anchor — it
 * survives renewal, unlike the leaf. Mirrors the server's mintAnchorLeaf.
 * @returns {Promise<string>} signed anchor JWT
 */
export async function createRootAnchor(scope, { name, note } = {}) {
    await acequiaReady()
    const userId = getUserId()
    const userKeys = getUserKeys()
    if (!userKeys) {
        throw new Error('User keys not available — register or link a device first')
    }
    const userPublicJWK = await jose.exportJWK(userKeys.publicKey)
    const kid = await jose.calculateJwkThumbprint(userPublicJWK)

    const anchor = new jose.SignJWT({
        scope: scopeForPayload(scope),
        depth: 0,
        max_depth: DEFAULT_MAX_DEPTH,
        attestation: { method: 'self-signed-root', createdAt: new Date().toISOString() },
        ...(name ? { name } : {}),
        ...(note ? { note } : {}),
    })
        .setSubject(userId)
        .setIssuedAt()
        .setProtectedHeader({ alg: 'PS256', kid, typ: 'anchor' })
    // No exp — anchors are inert; the leaf carries the working expiry.
    return await anchor.sign(userKeys.privateKey)
}

/**
 * Create a presentable LEAF hanging off an anchor (anchor model). Carries the
 * `anchor` pointer, scope, and a working `exp`; no name/note (those live on the
 * anchor). Signed with the user's own key.
 * @param {string} anchorHash
 * @param {object} scope
 * @param {number} expiresInSeconds
 * @param {object} [opts]
 * @param {string} [opts.aud] - the audience; defaults to this page's hostname. A peer
 *   grant names its app ('geocamera', 'debug-console') — auth/grants.js
 * @param {object} [opts.claims] - extra claims (a peer grant's `grantee`); they can't
 *   override the leaf's own (anchor, scope, sub, iat, exp)
 * @returns {Promise<string>} signed leaf JWT
 */
export async function createLeafForAnchor(anchorHash, scope, expiresInSeconds, { aud, claims, signer } = {}) {
    // The leaf carries the model's ONLY working expiry (anchors have none), so
    // an exp-less leaf would be an eternal credential. Verifiers refuse leaves
    // without exp; refuse to mint one here too (Phase 3.5).
    if (typeof expiresInSeconds !== 'number' || expiresInSeconds <= 0) {
        throw new Error('createLeafForAnchor: expiresInSeconds (positive number) is required — leaves must be time-boxed')
    }
    // ...and boxed within the one-year ceiling the server enforces at verify
    // (direct-user-tokens-cannot-be-revoked, 2026-09-14).
    if (expiresInSeconds > MAX_LEAF_TTL) {
        throw new Error('createLeafForAnchor: expiresInSeconds exceeds the one-year ceiling — the server refuses leaves with exp more than a year out')
    }
    await acequiaReady()
    // A leaf is signed by its anchor's user: `signer` names a kept identity
    // when the anchor predates a device link (identities.js)
    const userId = signer?.userId || getUserId()
    const userKeys = signer?.userKeys || getUserKeys()
    if (!userKeys) {
        throw new Error('User keys not available — register or link a device first')
    }
    const userPublicJWK = await jose.exportJWK(userKeys.publicKey)
    const kid = await jose.calculateJwkThumbprint(userPublicJWK)
    const now = Math.floor(Date.now() / 1000)

    const leaf = new jose.SignJWT({
        ...(claims || {}),
        anchor: anchorHash,
        scope: scopeForPayload(scope),
    })
        .setSubject(userId)
        .setIssuedAt()
        .setAudience(aud || (typeof location !== 'undefined' ? location.hostname : 'unknown'))
        .setProtectedHeader({ alg: 'PS256', kid })
        .setExpirationTime(now + expiresInSeconds)
    return await leaf.sign(userKeys.privateKey)
}

/**
 * Store an anchor on the server at PUT /auth/anchors/{hash}. Content-addressed
 * and inert — no short code is returned.
 * @returns {Promise<{ hash: string }>}
 */
export async function storeAnchor(authUrl, authToken, anchorJwt) {
    const hash = await hashToken(anchorJwt)
    const resp = await fetch(`${authUrl}/anchors/${hash}`, {
        method: 'PUT',
        headers: storeHeaders(authToken, { 'Content-Type': 'text/plain' }),
        body: anchorJwt,
    })
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.message || `Failed to store anchor: ${resp.status}`)
    }
    const data = await resp.json()
    return { hash: data.hash || hash }
}

export async function createChainToken({ authUrl, authToken, name, note, scope, expiresInSeconds, reuseId }) {
    // Anchor-model local mint (Phase 2 completion): a self-signed root anchor
    // (inert, no exp, no code) plus a presentable leaf hanging off it (with the
    // short code). The anchor is signed by the user's own key, so the session
    // token's remaining lifetime says nothing about the token's TTL
    // (token-cookie-security, 2026-07-28). Renewal mints a fresh leaf under the
    // SAME anchor — see renewChainToken.

    // Leaves must be time-boxed — anchors carry no exp, so the leaf's is the
    // model's only working one and createLeafForAnchor refuses to mint without.
    //
    // "Not specified" and "no expiry, please" are different requests and get
    // different answers. Omitting the TTL means "use the default", which is
    // fine. Asking for an eternal leaf is asking for something this model
    // cannot express, and substituting the default for it — which this did —
    // silently hands back a credential that expires in a week while the caller
    // believes it never will. The dashboard's "Never" option was doing exactly
    // that. Mirrors resolveExpiration's refusal of expiresTime 0 on the user
    // token side (require-exp-on-user-tokens).
    if (expiresInSeconds === undefined) {
        expiresInSeconds = DEFAULT_RENEWAL_TTL
    } else if (typeof expiresInSeconds !== 'number' || expiresInSeconds <= 0) {
        throw new Error(
            'createChainToken: leaves must be time-boxed — pass a positive expiresInSeconds, ' +
            'or omit it for the default TTL. There is no no-expiry option.'
        )
    }
    // A delegation nobody can identify in the dashboard is one nobody will
    // knowingly revoke. Every caller in the tree names what it mints; a dozen
    // unnamed Full-Access grants on production (abandoned-grants-keep-renewing,
    // 2026-10-16) came from a path that did not, so the mint refuses rather
    // than letting the next one in quietly.
    if (typeof name !== 'string' || !name.trim()) {
        throw new Error('createChainToken: a name is required — the dashboard lists grants by it, and an unnamed delegation cannot be knowingly revoked')
    }

    // 1. Create + store the root anchor (durable labels/provenance ride here).
    const anchorJwt = await createRootAnchor(scope, { name, note })
    const anchor = await storeAnchor(authUrl, authToken, anchorJwt)

    // 2. Create + store the leaf (the presentable credential; gets a short code).
    const leafJwt = await createLeafForAnchor(anchor.hash, scope, expiresInSeconds)
    const leaf = await storeChainToken(authUrl, authToken, leafJwt, { reuseId })

    // 3. Save wallet entry. rootHash is kept as an alias of anchorHash for
    //    back-compat with readers that key on it; both point at the anchor.
    const now = new Date().toISOString()
    const expiresAt = expiresInSeconds
        ? new Date(Date.now() + expiresInSeconds * 1000).toISOString()
        : null

    const walletEntry = {
        leafHash: leaf.hash,
        leafId: leaf.id,
        anchorHash: anchor.hash,
        rootHash: anchor.hash,
        leafJwt,
        // The anchor JWT itself, not just its hash. Without this the instance
        // has to fetch GET /auth/anchors/:hash to verify a token it minted
        // moments ago — going to the network for data it produced. Anchors are
        // inert (never presentable) and content-addressed, so holding one costs
        // nothing and cannot be substituted undetectably. Renewal carries it
        // through: a new leaf hangs off the SAME anchor.
        anchorJwt,
        name,
        note: note || null,
        scope,
        createdAt: now,
        expiresAt,
        status: 'active',
        revokedAt: null,
        // Renewal policy, three states (see runRenewalCheck): true = always
        // renew, null/absent = renew if used, false = lapse at expiry. The
        // library default is "if used": a caller that says nothing gets a
        // grant that lapses when abandoned. Standing access is an opt-up —
        // the dashboard writes its creator's choice right after minting.
        // (Until 2026-10-19 the default here was `true`, so every grant minted
        // outside the dashboard renewed forever, used or not.)
        autoRenew: null,
        ttlSeconds: expiresInSeconds || null,
    }

    if (walletStore) {
        // Keyed by the ANCHOR (Phase 5 re-key): one delegation, one row,
        // forever — renewal updates this row in place instead of the old
        // delete-and-reinsert under a rotating leaf hash.
        await walletStore.setItem(anchor.hash, walletEntry)
    }

    return { anchorHash: anchor.hash, rootHash: anchor.hash, leafHash: leaf.hash, leafJwt, leafId: leaf.id, expiresInSeconds }
}

// --- Chain Renewal (token-cookie-security Design §1a) ---

// Default leaf lifetime, and the fallback when an old leaf has no exp/iat.
//
// 7 days since 2026-08-25, down from 30. Signed revocation objects made the
// posture explicit — offline, an unreachable revocation source does not refuse
// a credential — and that makes **expiry the entire bound** on a stale grant
// rather than a backstop behind propagation. 30 days was chosen when the server
// was assumed reachable; it is far too long to be the only thing standing
// between a revoked delegation and a peer that never heard about it.
//
// Retuned as a set with the server's RENEWAL_WINDOW_SECONDS (2d) and
// EXPIRED_TOKEN_GRACE_SECONDS (3d) — see the invariants stated there. In short:
// TTL must exceed the renewal window (or every token is due at birth), and
// TTL - window must be at least YOUNG_WINDOW below (or abandoned grants renew
// forever). Lower this further only by moving those together.
export const DEFAULT_RENEWAL_TTL = 7 * 24 * 60 * 60
// One-year exp ceiling — mirrors MAX_TOKEN_TTL_SECONDS in localWebDAV
// src/auth/requireExp.mjs (direct-user-tokens-cannot-be-revoked, 2026-09-14).
export const MAX_LEAF_TTL = 365 * 24 * 60 * 60
// Use-aware renewal window: tokens neither wallet-flagged, young, nor
// used by the server within this window lapse instead of renewing.
const USE_WINDOW_SECONDS = 30 * 24 * 60 * 60
// Young grace: a freshly minted token may not have been handed out yet, so it
// renews without use history.
//
// The loophole this guards against: if renewal fires while a grant still reads
// young, and the clock it is judged by resets on every renewal, an abandoned
// delegation renews forever. Anchor clocks (Phase 5) fixed that for the anchor
// model by reading the ANCHOR's iat, which never resets — but a legacy grant
// with no anchorIat still falls back to the leaf's, so the arithmetic has to
// keep working: renewal must fire only AFTER the young window has passed, i.e.
// TTL - RENEWAL_WINDOW >= YOUNG_WINDOW.
//
// At 30d TTL / 7d window that read "5 days exempts nothing with TTL >= 12d".
// At 7d TTL / 2d window the budget is 7 - 2 = 5 days, which this meets exactly:
// a never-used grant is no longer young when its first renewal comes due, so it
// lapses as intended. Exactly-met means there is no slack — lowering the TTL
// again without lowering this reopens the loophole.
// See projects/docs/2026/08/12/use-aware-renewal-young-token-loophole.md
export const YOUNG_WINDOW_SECONDS = 5 * 24 * 60 * 60

/**
 * Renew one chain token: re-mint root + leaf with the same scope and TTL,
 * store both, and repoint the stable leafId at the new leaf. Holders of
 * `?token=<leafId>` links follow the renewal with no action on their
 * side; the wallet entry is rewritten under the new leaf hash.
 *
 * Requires the user's private key (re-minting is always client-side).
 *
 * @param {Object} options
 * @param {string} options.authUrl - Base auth URL (e.g., '/auth')
 * @param {string} options.authToken - Bearer token for authentication
 * @param {string} options.leafId - Stable CUID2 id to renew
 * @param {string} [options.leafJwt] - Old leaf JWT (fetched by id if omitted)
 */
export async function renewChainToken({ authUrl, authToken, leafId, leafJwt }) {
    if (!leafJwt) {
        const resp = await fetch(`${authUrl}/chains/id/${encodeURIComponent(leafId)}`)
        if (!resp.ok) throw new Error(`Cannot resolve leafId ${leafId}: ${resp.status}`)
        leafJwt = await resp.text()
    }
    const payload = jose.decodeJwt(leafJwt)
    // Carry the outgoing TTL, clamped to the one-year ceiling so a pre-ceiling
    // leaf is not renewed into something the server refuses.
    const ttl = Math.min(
        payload.exp && payload.iat ? payload.exp - payload.iat : DEFAULT_RENEWAL_TTL,
        MAX_LEAF_TTL
    )
    const scope = payload.scope || { paths: [], writePaths: [] }
    const oldHash = await hashToken(leafJwt)

    // Carry name/note through the renewal — payload first, wallet as
    // backfill for tokens minted before names rode in the JWT. Anchor-model
    // rows are keyed by the anchor (Phase 5 re-key); a pre-re-key row may
    // still sit under the old leaf hash and migrates below.
    let oldWallet = null
    let oldWalletKey = null
    try {
        if (walletStore) {
            if (payload.anchor !== undefined) {
                oldWallet = await walletStore.getItem(payload.anchor)
                if (oldWallet) oldWalletKey = payload.anchor
            }
            if (!oldWallet) {
                oldWallet = await walletStore.getItem(oldHash)
                if (oldWallet) oldWalletKey = oldHash
            }
            // A row can sit under another key: the dashboard patches a row in
            // place after a server-side scope-edit, which moves the grant onto
            // a new anchor while the row keeps the old anchor as its key. Find
            // it by its fields; the re-key below then moves it onto the anchor.
            if (!oldWallet) {
                const row = await resolveWalletRow(payload.anchor ?? oldHash)
                    || (payload.anchor !== undefined ? await resolveWalletRow(oldHash) : null)
                if (row) { oldWallet = row.entry; oldWalletKey = row.key }
            }
        }
    } catch (e) { /* wallet read failure — renew unnamed */ }
    // Never resurrect: a wallet row marked revoked is a decision, and renewal
    // must not write status:'active' over it. (Server-side revoked LINEAGES
    // are refused at the repoint regardless — this guards the local-only
    // revoke mark too.)
    if (oldWallet?.status === 'revoked') {
        throw new Error(`Token ${leafId} is revoked — renewal refused; re-grant by minting a new token`)
    }

    // Mint the replacement leaf: a NEW leaf under the SAME anchor. The anchor
    // never moves — that is precisely why anchor-keyed revocation survives
    // renewal — and its labels/provenance are inherited for free. A legacy
    // `parent` leaf cannot renew: the legacy root+leaf re-mint went with the
    // dual-accept window (2026-10-06), the server refuses storing legacy
    // shapes, and the grant itself no longer verifies — the holder re-grants
    // by minting a new (anchor-model) delegation.
    //
    // The new leaf is stored WITH the stable id (reuseId = the repoint):
    // every ?token=<leafId> holder is silently on the new token, and no
    // second code is minted. Codes are credentials — renewal must not create
    // one; the old path (store fresh, then reassign) orphaned a resolvable
    // extra code every renewal.
    if (payload.anchor === undefined) {
        throw new Error(`Token ${leafId} is a legacy (parent-linked) grant — it no longer verifies and cannot renew; mint a new delegation`)
    }
    // The replacement is the same credential with a new expiry: it keeps the
    // outgoing leaf's audience and every claim of the leaf's own (an app
    // grant's `type`, `grantee`, `granteeName`…). createLeafForAnchor sets
    // anchor/scope/sub/iat/exp over them. Until 2026-10-19 the renewal minted
    // a bare hostname-audience leaf, so an app grant it renewed came back
    // one its app's verifier refused.
    const { anchor: _anchor, scope: _scope, sub: _sub, iat: _iat, exp: _exp, nbf: _nbf, aud, ...carried } = payload
    const newLeafJwt = await createLeafForAnchor(payload.anchor, scope, ttl, { aud, claims: carried })
    const leaf = await storeChainToken(authUrl, authToken, newLeafJwt, { reuseId: leafId })
    const anchorHashForWallet = payload.anchor

    // Update the wallet row. Grants are keyed by the ANCHOR — one delegation,
    // one row, updated in place across renewals (Phase 5 re-key); a pre-re-key
    // row found under the old leaf hash migrates onto the anchor key here.
    if (walletStore) {
        const old = oldWallet
        const nowIso = new Date().toISOString()
        const storeKey = payload.anchor
        const entry = {
            ...(old || {}),
            leafHash: leaf.hash,
            leafId,
            anchorHash: anchorHashForWallet,
            rootHash: anchorHashForWallet,
            leafJwt: newLeafJwt,
            scope,
            renewedAt: nowIso,
            expiresAt: new Date(Date.now() + ttl * 1000).toISOString(),
            status: 'active',
        }
        if (!old) {
            // Renewed a token created on another device — minimal record
            entry.name = entry.name || `renewed-${leafId.slice(0, 8)}`
            entry.createdAt = entry.createdAt || nowIso
            // No autoRenew: this device renewed because the grant was young or
            // in use, which is not a decision to keep it alive regardless.
            // Writing `true` here made every device that renewed a grant once
            // renew it forever, used or not (standing-access, 2026-09-24).
            entry.ttlSeconds = entry.ttlSeconds ?? ttl
        }
        await walletStore.setItem(storeKey, entry)
        if (oldWalletKey && oldWalletKey !== storeKey) {
            await walletStore.removeItem(oldWalletKey)
        }
    }

    return { leafId, leafHash: leaf.hash, leafJwt: newLeafJwt, oldHash, expiresInSeconds: ttl }
}

/**
 * One renewal pass against a server's worklist: fetch
 * GET {authUrl}/chains/renewals-due and renew every leaf belonging to
 * this user, unless its wallet entry opts out (autoRenew === false) or
 * is revoked locally. Root links renew implicitly with their leaf; the
 * server excludes revoked tokens from the worklist.
 *
 * The wallet's `autoRenew` has three states: `true` renews regardless of
 * use, `false` never renews, and null/absent (or no row at all) applies the
 * use-aware policy — renew only a young or recently used grant.
 *
 * The server registry (not the local wallet) is the source of what's
 * due, so tokens minted on another linked device renew here too.
 *
 * @param {Object} [options]
 * @param {string} [options.authUrl='/auth']
 * @param {string} [options.authToken] - defaults to a short-lived
 *   self-minted root token (payload kid + sub routes as a user token)
 * @returns {Promise<{renewed: number, skipped: number, failed: number}>}
 */
export async function runRenewalCheck({ authUrl = '/auth', authToken } = {}) {
    await acequiaReady()
    const userId = getUserId()
    const results = { renewed: 0, skipped: 0, lapsed: 0, failed: 0, migrated: 0 }
    if (!userId || !getUserKeys()) return results

    if (!authToken) authToken = await createRootToken(300)

    const resp = await fetch(`${authUrl}/chains/renewals-due`, {
        headers: { Authorization: `Bearer ${authToken}` },
    })
    if (!resp.ok) throw new Error(`renewals-due failed: ${resp.status}`)
    const { due } = await resp.json()

    const nowSec = Math.floor(Date.now() / 1000)
    const host = authHostOf(authUrl)
    for (const entry of due || []) {
        if (!entry.isLeaf || !entry.id || entry.sub !== userId) {
            results.skipped++
            continue
        }
        // An app's grant (acequia.grants: `aud` names the app, not this host)
        // is its grant manager's to renew — that engine keeps the app's claims
        // and reads the app's own use. This engine's clocks are the server's,
        // which never sees a peer-presented grant, and its renewal would be
        // judged on the wrong use. Leaves with no audience, or minted where
        // there was no hostname, are this host's.
        if (isForeignAudience(entry.aud, host)) {
            results.skipped++
            continue
        }
        let wallet = null
        try {
            // Wallet rows are keyed by anchor for anchor-model grants (one
            // delegation, one row) and by leaf hash for legacy ones.
            let walletKey = null
            if (walletStore) {
                if (entry.anchorHash) { wallet = await walletStore.getItem(entry.anchorHash); walletKey = entry.anchorHash }
                if (!wallet) { wallet = await walletStore.getItem(entry.hash); walletKey = entry.hash }
            }
            if (wallet && (wallet.autoRenew === false || wallet.status === 'revoked')) {
                results.skipped++
                continue
            }
            // Residue of the pre-2026-09-24 build: a device that adopted a
            // grant it had no row for wrote `renewed-<id>` with autoRenew:
            // true, and renewed it forever from then on. Nobody chose
            // "always" for a row named by a machine, so a never-used one
            // falls back to "if used" here — once, in place. A row the owner
            // has since renamed, or whose grant has seen use, is left alone.
            if (
                wallet && wallet.autoRenew === true && /^renewed-/.test(wallet.name || '') &&
                !entry.lastUsed && !entry.anchorLastUsed
            ) {
                wallet = { ...wallet, autoRenew: null }
                await walletStore.setItem(walletKey, wallet)
                results.migrated++
            }
        } catch (e) {
            // wallet read failure — fall through to the use-aware policy
        }
        // Use-aware policy: abandoned delegations dry up instead of
        // renewing forever (headgate semantics). Renew when the wallet
        // explicitly wants it (autoRenew: true), when the GRANT is young
        // (freshly created, possibly not yet handed out), or when it was
        // used within the window.
        //
        // Anchor clocks (Phase 5): "young" reads the ANCHOR's iat — the
        // delegation's durable creation time — closing the young-token
        // loophole (leaf iat resets every renewal, so a default-TTL token
        // was perpetually young; see use-aware-renewal-young-token-loophole).
        // "Used" reads either clock: the anchor stamp survives renewal, so a
        // delegation in real use cannot read as abandoned just because its
        // leaf rotated. Renewal itself touches neither (renewal ≠ use).
        const walletForces = wallet?.autoRenew === true
        const bornAt = entry.anchorIat ?? entry.iat
        const young = bornAt && nowSec - bornAt < YOUNG_WINDOW_SECONDS
        const usedRecently =
            (entry.lastUsed && nowSec - entry.lastUsed < USE_WINDOW_SECONDS) ||
            (entry.anchorLastUsed && nowSec - entry.anchorLastUsed < USE_WINDOW_SECONDS)
        if (!walletForces && !young && !usedRecently) {
            results.lapsed++
            continue
        }
        try {
            await renewChainToken({ authUrl, authToken, leafId: entry.id })
            results.renewed++
        } catch (e) {
            console.warn(`[chains] renewal failed for ${entry.id}:`, e.message)
            results.failed++
        }
    }
    return results
}

/** The host an auth URL serves — the audience a leaf minted on it carries. */
function authHostOf(authUrl) {
    try {
        const base = typeof location !== 'undefined' ? location.origin : undefined
        return new URL(authUrl, base).hostname || null
    } catch {
        return null
    }
}

/**
 * Whether a leaf's audience is some other party's: an app ('geocamera',
 * 'debug-console') rather than this host. No audience, the Node minter's
 * 'unknown', or no known host all read as this host's.
 */
export function isForeignAudience(aud, host) {
    if (!aud || !host) return false
    const list = [].concat(aud)
    if (list.includes(host) || list.includes('unknown')) return false
    return true
}

let renewalTimer = null

/**
 * Start periodic renewal checks (runs one immediately). Singleton — a
 * second call replaces the first. Triggered by <acequia-node> on join;
 * the SW runs its own throttled checks on wake.
 */
export function startAutoRenewal({ authUrl = '/auth', intervalMs = 6 * 60 * 60 * 1000 } = {}) {
    stopAutoRenewal()
    const run = () => runRenewalCheck({ authUrl }).catch(() => {})
    run()
    renewalTimer = setInterval(run, intervalMs)
}

export function stopAutoRenewal() {
    if (renewalTimer) {
        clearInterval(renewalTimer)
        renewalTimer = null
    }
}

// --- Scope Verification (client-side) ---

/**
 * Check if a request path matches any of the allowed path patterns.
 * Mirrors the server's matchesPath() in jwtAuth.mjs.
 *
 * @param {string} requestPath - The actual request path (e.g., '/docs/file.txt')
 * @param {string[]} allowedPaths - Array of path patterns (e.g., ['/docs/*', '/public/*'])
 * @returns {boolean}
 */
export function matchesPath(requestPath, allowedPaths) {
    if (!allowedPaths || allowedPaths.length === 0) return false

    // Trailing slash never flips a decision; a `/prefix/*` grant covers the
    // collection root `/prefix` itself. Mirror of the server's matchesPath
    // (copy-and-move-under-grant-triples issue B — keep in lockstep).
    const req = requestPath !== '/' ? requestPath.replace(/\/+$/, '') : '/'

    for (const permPath of allowedPaths) {
        if (permPath === '*') return true
        const perm = permPath !== '/' ? permPath.replace(/\/+$/, '') : '/'
        if (perm === req) return true
        if (perm.endsWith('/*')) {
            const base = perm.slice(0, -2)
            if (req === base || req.startsWith(base + '/')) return true
        }
    }
    return false
}

/**
 * Check if requestPath is an ancestor directory of any scope path.
 * Allows navigating to parent directories when a user has access
 * to a subdirectory (e.g., scope '/ants/*' allows PROPFIND on '/').
 * Mirrors the server's isAncestorOfScope() in auth/utils.mjs.
 *
 * @param {string} requestPath - The path being accessed (e.g., '/')
 * @param {string[]} scopePaths - The allowed scope paths (e.g., ['/ants/*'])
 * @returns {boolean}
 */
export function isAncestorOfScope(requestPath, scopePaths) {
    if (!scopePaths || scopePaths.length === 0) return false
    const normalizedReq = requestPath === '/' ? '/' : requestPath.replace(/\/$/, '') + '/'

    for (const scopePath of scopePaths) {
        const baseDir = scopePath.endsWith('/*') ? scopePath.slice(0, -1) : scopePath
        const normalizedBase = baseDir.endsWith('/') ? baseDir : baseDir + '/'
        if (normalizedBase.startsWith(normalizedReq)) return true
    }
    return false
}

/**
 * Check if childScope is a subset of parentScope.
 * Each child path must be covered by at least one parent path.
 * Mirrors the server's isScopeSubset() in chains.mjs.
 *
 * @param {{ paths: string[], writePaths: string[] }} childScope
 * @param {{ paths: string[], writePaths: string[] }} parentScope
 * @returns {boolean}
 */
// ---------------------------------------------------------------------------
// Grant triples & canonical form (scope-privileges project). MIRRORED from
// localWebDAV/src/chains.mjs — keep the two byte-identical in behaviour.
// See the project doc's "Canonical form" section.
// ---------------------------------------------------------------------------

export const READ_PRIVILEGES = ['read']
export const WRITE_PRIVILEGES = ['bind', 'unbind', 'write-content', 'write-properties']

// Method → required privilege. The one shared table (resolves the old
// server/client method-list divergence). COPY/MOVE are composite and handled
// by the caller (source vs destination legs), never binned here.
export const METHOD_PRIVILEGE = {
    GET: 'read', HEAD: 'read', OPTIONS: 'read', PROPFIND: 'read', REPORT: 'read',
    PUT: 'write-content', // create-vs-overwrite refined at enforcement
    MKCOL: 'bind',
    DELETE: 'unbind',
    PROPPATCH: 'write-properties',
    LOCK: 'write-content', UNLOCK: 'write-content',
    PATCH: 'write-content', POST: 'write-content',
}

/** Privilege a method requires, or null if unknown/composite. */
export function privilegeForMethod(method) {
    return METHOD_PRIVILEGE[method] || null
}

function sortedUnique(arr) {
    return [...new Set(arr || [])].sort()
}

function canonicalizeConditions(conditions) {
    if (!conditions || typeof conditions !== 'object') return undefined
    const keys = Object.keys(conditions).sort()
    if (keys.length === 0) return undefined
    const out = {}
    for (const k of keys) {
        const v = conditions[k]
        out[k] = Array.isArray(v) ? sortedUnique(v) : v
    }
    return out
}

const conditionsString = (c) => (c ? JSON.stringify(c) : '')

/**
 * Normalize any scope (legacy {paths,writePaths} or {grants}) to the
 * canonical grant-set. Idempotent; expansion-stable.
 */
export function canonicalizeScope(scope) {
    if (!scope) return { grants: [] }

    const raw = []
    if (Array.isArray(scope.grants)) {
        for (const g of scope.grants) {
            if (!g || !g.pattern) continue
            raw.push({
                privileges: g.privileges || [],
                pattern: g.pattern,
                conditions: canonicalizeConditions(g.conditions),
            })
        }
    }
    for (const p of scope.paths || [])
        raw.push({ privileges: READ_PRIVILEGES, pattern: p, conditions: undefined })
    for (const w of scope.writePaths || [])
        raw.push({ privileges: WRITE_PRIVILEGES, pattern: w, conditions: undefined })

    const byKey = new Map()
    for (const g of raw) {
        const key = `${g.pattern} ${conditionsString(g.conditions)}`
        const existing = byKey.get(key)
        if (existing) {
            existing.privileges = sortedUnique([...existing.privileges, ...g.privileges])
        } else {
            byKey.set(key, {
                privileges: sortedUnique(g.privileges),
                pattern: g.pattern,
                conditions: g.conditions,
            })
        }
    }

    const grants = [...byKey.values()].map((g) =>
        g.conditions
            ? { privileges: g.privileges, pattern: g.pattern, conditions: g.conditions }
            : { privileges: g.privileges, pattern: g.pattern }
    )
    grants.sort((a, b) => {
        if (a.pattern !== b.pattern) return a.pattern < b.pattern ? -1 : 1
        const ca = conditionsString(a.conditions), cb = conditionsString(b.conditions)
        if (ca !== cb) return ca < cb ? -1 : 1
        const pa = a.privileges.join(','), pb = b.privileges.join(',')
        return pa < pb ? -1 : pa > pb ? 1 : 0
    })
    return { grants }
}

/** Stable string of a scope's canonical grants — for hashing and equality. */
export function scopeCanonicalKey(scope) {
    return JSON.stringify(canonicalizeScope(scope).grants)
}

function conditionsAtLeastAsRestrictive(childCond, parentCond) {
    if (!parentCond) return true
    for (const [k, pv] of Object.entries(parentCond)) {
        const cv = childCond?.[k]
        if (cv === undefined) return false
        if (k === 'max-size') {
            if (typeof cv !== 'number' || cv > pv) return false
        } else if (Array.isArray(pv)) {
            const pset = new Set(pv)
            if (!Array.isArray(cv) || !cv.every((x) => pset.has(x))) return false
        } else if (cv !== pv) {
            return false
        }
    }
    return true
}

export function isScopeSubset(childScope, parentScope) {
    if (!childScope) return true
    if (!parentScope) return false

    const child = canonicalizeScope(childScope).grants
    const parent = canonicalizeScope(parentScope).grants

    for (const cg of child) {
        // The UNION of the covering parent grants' privileges must cover the
        // child grant's — a parent whose read and write are separate grants
        // (`paths ["*"], writePaths ["/a/*"]`) covers a read+write child on
        // `/a/x/*`, which canonicalizes to one grant no single parent grant
        // covers. Mirrors localWebDAV/src/chains.mjs.
        const pprivs = new Set()
        for (const pg of parent) {
            if (!isPathCovered(cg.pattern, [pg.pattern])) continue
            if (!conditionsAtLeastAsRestrictive(cg.conditions, pg.conditions)) continue
            for (const p of pg.privileges) pprivs.add(p)
        }
        if (!cg.privileges.every((p) => pprivs.has(p))) return false
    }
    return true
}

function isPathCovered(childPath, parentPaths) {
    for (const parentPath of parentPaths) {
        if (parentPath === '*') return true
        if (parentPath === childPath) return true
        if (parentPath.endsWith('/*') && childPath.startsWith(parentPath.slice(0, -1))) {
            return true
        }
        if (parentPath.endsWith('/*') && childPath.endsWith('/*') &&
            childPath.startsWith(parentPath.slice(0, -1))) {
            return true
        }
    }
    return false
}

/**
 * Patterns in a scope that grant ANY of the given privileges. Canonicalizes,
 * so legacy {paths,writePaths} and triple {grants} both work — the mirror of
 * the server's pathsForPrivilege (jwtAuth.mjs).
 */
export function pathsForPrivileges(scope, privileges) {
    const want = new Set(privileges)
    return canonicalizeScope(scope)
        .grants.filter((g) => g.privileges.some((p) => want.has(p)))
        .map((g) => g.pattern)
}

/**
 * Verify that a request is authorized by a chain token's scope. The
 * client-side equivalent of the server's canAccess/canWrite (scope-privileges):
 * the method maps to a required privilege, checked against the canonical
 * grants. Writes do NOT also require read (a bind-only drop-box deposits
 * without read), matching the server since 2026-08-21.
 *
 * @param {string} method - HTTP method (GET, PUT, DELETE, PROPFIND, etc.)
 * @param {string} requestPath - The request path
 * @param {object} scope - Token's effective scope (legacy or triple)
 * @param {{ exists?: boolean, ancestors?: boolean }} [opts] - For PUT: whether the
 *   target already exists (create=bind vs overwrite=write-content). Unknown →
 *   either grant suffices for this pre-flight; the server refines by existence.
 *   `ancestors: false` drops the WebDAV navigation rule (a read of an ancestor
 *   of a granted path) — for peer routes, where there is nothing to navigate.
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function checkScopeAccess(method, requestPath, scope, { exists, ancestors = true } = {}) {
    if (!scope) return { allowed: false, reason: 'No scope provided' }

    // Method → acceptable privilege(s).
    let accept
    if (method === 'PUT') {
        accept = exists === true ? ['write-content']
            : exists === false ? ['bind']
            : ['bind', 'write-content']
    } else if (method === 'COPY') {
        // COPY reads its source; the destination leg is checked separately
        // (checkTransferAccess). Decomposed to match the server rather than
        // the old coarse any-write (copy-and-move-under-grant-triples issue C).
        accept = ['read']
    } else if (method === 'MOVE') {
        // MOVE unlinks its source — `unbind`, not any-write.
        accept = ['unbind']
    } else {
        const p = privilegeForMethod(method)
        accept = p === null ? [...WRITE_PRIVILEGES] : [p]
    }

    const paths = pathsForPrivileges(scope, accept)
    if (matchesPath(requestPath, paths)) return { allowed: true }

    // Reads may navigate to ancestors of a granted path.
    const isRead = accept.length === 1 && accept[0] === 'read'
    if (ancestors && isRead && isAncestorOfScope(requestPath, paths)) return { allowed: true }

    return { allowed: false, reason: `${accept.join('/')} access denied for path: ${requestPath}` }
}

/**
 * Authorize a COPY/MOVE across both legs — the client mirror of the server's
 * checkTransferAuthorization (copy-and-move-under-grant-triples issue C).
 * A transfer touches two resources, so it gets two decisions:
 *   COPY: read @source  + bind @dest (+ unbind @dest when it exists)
 *   MOVE: unbind @source + bind @dest (+ unbind @dest when it exists)
 * The destination overwrite costs `unbind` because RFC 4918 §9.8.4/§9.9.3
 * DELETE an existing destination before the operation — so a `maintain` grant
 * (write-content, no unbind) cannot replace a collection.
 *
 * @param {object} scope
 * @param {{ method: 'COPY'|'MOVE', sourcePath: string, destPath: string, destExists?: boolean }} req
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function checkTransferAccess(scope, { method, sourcePath, destPath, destExists }) {
    if (!scope) return { allowed: false, reason: 'No scope provided' }
    const source = checkScopeAccess(method, sourcePath, scope)
    if (!source.allowed) {
        return { allowed: false, reason: `source: ${source.reason}` }
    }
    const bind = pathsForPrivileges(scope, ['bind'])
    if (!matchesPath(destPath, bind)) {
        return { allowed: false, reason: `destination: bind access denied for path: ${destPath}` }
    }
    if (destExists) {
        const unbind = pathsForPrivileges(scope, ['unbind'])
        if (!matchesPath(destPath, unbind)) {
            return {
                allowed: false,
                reason: `destination exists; replacing it requires unbind at: ${destPath}`,
            }
        }
    }
    return { allowed: true }
}

/**
 * Decode a chain token and extract its scope without full verification.
 * Useful for quick scope checks when the token has already been verified
 * or for UI display purposes.
 *
 * @param {string} jwt - The JWT string
 * @returns {{ payload: Object, scope: { paths: string[], writePaths: string[] } | null }}
 */
export function decodeTokenScope(jwt) {
    const payload = jose.decodeJwt(jwt)
    return {
        payload,
        scope: payload.scope || null,
    }
}

// --- Chain Resolution & Verification ---

// Exported so the revocation authority walk bounds itself the same way: it
// climbs the same ancestry, so a second constant would be a second answer.
export const MAX_CHAIN_LENGTH = 5 // depths 0..3 = 4 tokens, plus 1 for safety
const DEFAULT_MAX_DEPTH = 4

/**
 * Resolve a content-addressed chain link from local storage.
 *
 * Anchors are named by the SHA-256 of their own bytes, so a locally held link
 * is only usable if it still hashes to the name it was asked for. That check is
 * what makes local resolution safe: a corrupted row, or one planted by
 * something with wallet access, cannot masquerade as an anchor it is not —
 * which is the property `resolveChain`'s network fetch was implicitly relying
 * on the server to provide.
 *
 * Returns null for "not held locally" — the caller falls back to the network.
 * Local-*first*, never local-only: a chain may contain intermediates this
 * instance never minted.
 *
 * @param {string} hash - SHA-256 of the anchor JWT
 * @returns {Promise<string|null>} the anchor JWT, or null
 */
export async function resolveLinkLocally(hash) {
    if (!hash) return null

    // 1. The wallet — grants this instance minted or holds. Richer and
    //    authoritative, so it wins.
    if (walletStore) {
        try {
            // Anchor-model rows are keyed by anchor hash (Phase 5 re-key), so a
            // grant this instance minted is a direct hit.
            const row = await walletStore.getItem(hash)
            const jwt = row?.anchorJwt
            if (jwt && (await hashToken(jwt)) === hash) return jwt
        } catch { /* fall through */ }
    }

    // 2. The link cache — anything resolved before, including links from
    //    grants this instance does not hold (a server-minted grant has no
    //    wallet row on the peer verifying it).
    if (linkStore) {
        try {
            const jwt = await linkStore.getItem(hash)
            // Re-derived on read, not trusted from the key. The key is what was
            // asked for; the value is what is stored, and only hashing relates
            // them — so a tampered entry is unusable rather than believed.
            if (jwt && (await hashToken(jwt)) === hash) return jwt
        } catch { /* fall through to the network */ }
    }

    return null
}

/**
 * Backfill a link fetched over the network into the wallet row that needed it.
 *
 * Grants minted before anchorJwt was persisted hold only the hash, so they
 * would fetch the same immutable anchor on every verification forever —
 * renewal does not fix them either, since it carries the old row forward. This
 * converts such a grant on its first verification, at the cost of one write.
 *
 * Deliberately only fills rows that already exist: a wallet row means "a grant
 * I hold", and manufacturing rows for arbitrary anchors would turn the wallet
 * into a general link cache. That cache is `linkStore` above
 * (`acequia-chain-links`, since 2026-08-21), keyed by hash; `sw.js` carries a
 * second, URL-keyed copy for every acequia.js app that fetches a
 * content-addressed URL without going through this module.
 *
 * @param {string} hash
 * @param {string} jwt
 */
async function rememberLink(hash, jwt) {
    if (!hash || !jwt) return
    // Never store bytes under a name they do not hash to, even from the
    // server: resolveLinkLocally would reject them on read anyway, so a
    // mismatch means something is wrong rather than something to keep.
    let named = false
    try {
        named = (await hashToken(jwt)) === hash
    } catch { /* treat as unnamed */ }
    if (!named) return

    if (linkStore && jwt.length <= MAX_CACHED_LINK_BYTES) {
        try {
            await linkStore.setItem(hash, jwt)
        } catch { /* best-effort */ }
    }
    await backfillWalletRow(hash, jwt)
}

/**
 * Keep anchors a presenter handed over, once a chain built from them has
 * verified: a content-addressed link is immutable, so the next presentation
 * needs neither the presenter nor the network to resolve it (auth/grants.js).
 * Each is stored only under the hash it names.
 * @param {string[]} jwts
 */
export async function cacheLinks(jwts = []) {
    for (const jwt of jwts) {
        try {
            await rememberLink(await hashToken(jwt), jwt)
        } catch { /* best-effort */ }
    }
}

/**
 * Write a wallet row under a key — an anchor hash, for anchor-model grants.
 * For grants minted without the server (auth/grants.js); createChainToken
 * writes its own row.
 */
export async function saveWalletRow(key, row) {
    if (!walletStore) throw new Error('No wallet store in this context')
    await walletStore.setItem(key, row)
    return row
}

async function backfillWalletRow(hash, jwt) {
    if (!walletStore || !hash || !jwt) return
    try {
        const row = await walletStore.getItem(hash)
        if (!row || row.anchorJwt) return
        await walletStore.setItem(hash, { ...row, anchorJwt: jwt })
    } catch {
        // Best-effort: verification already succeeded without this.
    }
}

/**
 * @param {object} [options]
 * @param {Function} [options.resolveLink] - async (hash) => jwt|null, tried
 *   before the network. Defaults to the wallet-backed resolver above.
 */
/**
 * A link resolver that tries locally first, then the network — the full
 * resolution story, as one callable.
 *
 * This is what `resolveChain` has always done inline. Extracted because chain
 * verification is no longer the only thing that has to climb an ancestry: a
 * revocation's authority walk climbs the same anchors, and it cannot be
 * restricted to what happens to be cached without failing for exactly the
 * grants a peer does not hold — which is the population that matters.
 *
 * Local hits are not re-remembered; network hits are, since an anchor is
 * immutable and fetched once is fetched forever.
 *
 * Throws rather than returning null so the caller keeps the distinction the
 * chain walk reports: `.notFound` means the server answered and has no such
 * link, anything else means we could not ask. A caller that only needs
 * "resolved or not" can treat any throw as a miss.
 *
 * @param {string} authUrl
 * @param {object} [options]
 * @param {Function} [options.resolveLink] - the local half; defaults to the wallet+cache resolver
 * @returns {Function} async (hash) => jwt
 */
export function createLinkResolver(authUrl, { resolveLink = resolveLinkLocally } = {}) {
    return async function resolveLinkOrFetch(hash) {
        let jwt = null
        try {
            jwt = await resolveLink(hash)
        } catch {
            jwt = null // a failing resolver is a miss, not an error
        }
        if (jwt) return jwt

        let resp
        try {
            resp = await fetch(`${authUrl}/anchors/${hash}`)
        } catch (err) {
            throw new Error(`Failed to fetch anchor ${hash}: ${err.message}`)
        }
        if (!resp.ok) {
            const err = new Error(`Anchor not found: ${hash}`)
            err.notFound = true
            throw err
        }
        try {
            jwt = await resp.text()
        } catch (err) {
            throw new Error(`Failed to fetch anchor ${hash}: ${err.message}`)
        }
        // Immutable and content-addressed: fetched once is fetched forever.
        // Feeds the link cache, and fills a wallet row holding only the hash.
        await rememberLink(hash, jwt)
        return jwt
    }
}

export async function resolveChain(authUrl, leafJwt, { resolveLink = resolveLinkLocally } = {}) {
    let leafPayload
    try {
        leafPayload = jose.decodeJwt(leafJwt)
    } catch {
        return { chain: null, error: 'Invalid JWT in chain' }
    }

    // Anchor-model leaf (P2): it hangs off an anchor by an `anchor` claim and
    // has no `parent`. Resolve the anchor chain from the public anchor endpoint
    // (GET /auth/anchors/:hash) by walking anchor `parent` hashes to the root
    // anchor; the leaf is verified against the leaf anchor in resolveAndVerify.
    if (leafPayload.anchor !== undefined) {
        const anchorChain = []
        const resolveLinkOrFetch = createLinkResolver(authUrl, { resolveLink })
        let currentHash = leafPayload.anchor
        while (currentHash !== undefined) {
            let anchorJwt
            try {
                anchorJwt = await resolveLinkOrFetch(currentHash)
            } catch (err) {
                return { chain: null, error: err.message }
            }
            anchorChain.unshift(anchorJwt)
            if (anchorChain.length > MAX_CHAIN_LENGTH) {
                return { chain: null, error: 'Anchor chain exceeds maximum depth' }
            }
            let anchorPayload
            try {
                anchorPayload = jose.decodeJwt(anchorJwt)
            } catch {
                return { chain: null, error: 'Invalid anchor in chain' }
            }
            currentHash = anchorPayload.parent
        }
        return { chain: anchorChain, leaf: leafJwt, model: 'anchor' }
    }

    // Legacy parent-linked chains are REMOVED (2026-10-06,
    // close-the-dual-accept-window): the window closed early — live legacy
    // codes were retrofitted onto anchors and the remaining links revoked
    // fleet-wide. The legacy walk (fetching parents from GET
    // /auth/chains/:hash, which no longer serves them) is gone; a
    // `parent`-bearing token gets the migration-pointing refusal, and
    // anything else without an `anchor` claim is not a chain credential.
    if (leafPayload.parent !== undefined) {
        return { chain: null, error: LEGACY_CHAIN_MESSAGE }
    }
    return { chain: null, error: 'Not an anchor-model leaf — chains are anchor-model only (no anchor claim)' }
}

/**
 * Verify a resolved chain (ordered root-to-leaf).
 * Checks signatures, scope attenuation, expiry attenuation, depth limits,
 * and revocation at each link.
 *
 * @param {string[]} chain - Ordered [root, ..., leaf] JWT strings
 * @param {Object} options
 * @param {Function} options.resolvePublicKey - async (userId, kid) => { publicKey: JWK, status: string } | null
 * @param {Function} [options.isRevoked] - async (tokenHash) => boolean. Defaults to () => false.
 * @returns {Promise<{ valid: boolean, error?: string, leafPayload?, effectiveScope?, chainLength? }>}
 */
export async function verifyChain(chain, options = {}) {
    const { resolvePublicKey, isRevoked = async () => false, allowAnchorChain = false, revocationAudit } = options

    if (!resolvePublicKey) {
        return { valid: false, error: 'resolvePublicKey callback is required' }
    }
    if (!Array.isArray(chain) || chain.length === 0) {
        return { valid: false, error: 'Empty or invalid chain' }
    }
    if (chain.length > MAX_CHAIN_LENGTH) {
        return { valid: false, error: 'Chain exceeds maximum depth' }
    }

    // Fail closed on an anchor-terminated chain (Phase 3.5). The attack this
    // guards: a direct verifyChain caller that predates the leaf step would
    // treat a verified anchor chain as a verified CREDENTIAL, never checking
    // the leaf's signature, revocation, or expiry. (resolveChain's old
    // one-token fallthrough, which let a presented anchor resolve as its own
    // "chain", went with the dual-accept removal — this stays as defence in
    // depth.) Only resolveAndVerify opts in (allowAnchorChain) — it always
    // runs verifyLeafAgainstAnchor after.
    if (!allowAnchorChain && isPresentedAnchor(chain[chain.length - 1])) {
        return {
            valid: false,
            error: 'Chain ends in an anchor — anchors are not credentials; verify the presented leaf with resolveAndVerify',
        }
    }

    // --- Verify root (chain[0]) ---
    let rootPayload
    try {
        rootPayload = jose.decodeJwt(chain[0])
    } catch {
        return { valid: false, error: 'Root token is not a valid JWT' }
    }

    if (rootPayload.parent !== undefined) {
        return { valid: false, error: 'Root token must not have a parent claim' }
    }

    const rootUserId = rootPayload.sub
    // The signer is identified by the JWS protected header `kid`, not a payload
    // claim — the same place child links and leaves read it (kid-in-header
    // throughout). Legacy roots also carry a `kid` claim, harmlessly ignored.
    let rootKid
    try {
        rootKid = jose.decodeProtectedHeader(chain[0]).kid
    } catch {
        return { valid: false, error: 'Root token has an invalid header' }
    }
    if (!rootUserId) {
        return { valid: false, error: 'Root token missing sub claim' }
    }
    if (!rootKid) {
        return { valid: false, error: 'Root token missing kid header' }
    }

    // Resolve the root signer's public key
    const rootKeyInfo = await resolvePublicKey(rootUserId, rootKid)
    if (!rootKeyInfo) {
        return { valid: false, error: `Root signing key not found: ${rootKid} for user ${rootUserId}` }
    }
    if (rootKeyInfo.status === 'revoked') {
        return { valid: false, error: `Root signing key revoked: ${rootKid}` }
    }

    // Verify root signature
    let rootPublicKey
    try {
        rootPublicKey = await jose.importJWK(rootKeyInfo.publicKey, 'PS256')
    } catch {
        return { valid: false, error: 'Invalid root public key' }
    }

    try {
        await jose.jwtVerify(chain[0], rootPublicKey)
    } catch (err) {
        return { valid: false, error: `Root signature invalid: ${err.message}` }
    }

    // Check root revocation
    const rootHash = await hashToken(chain[0])
    if (await isRevoked(rootHash, revocationAudit)) {
        return { valid: false, error: 'Root token revoked' }
    }

    // Derive root scope: if root has explicit scope, use it;
    // otherwise assume full access (root creator is the authority)
    let parentScope
    if (rootPayload.scope) {
        parentScope = rootPayload.scope
    } else {
        parentScope = { paths: ['*'], writePaths: ['*'] }
    }

    // Single-token chain (root only) — valid as-is
    if (chain.length === 1) {
        return {
            valid: true,
            leafPayload: rootPayload,
            effectiveScope: parentScope,
            chainLength: 1,
            rootSub: rootUserId,
        }
    }

    // --- Verify each subsequent token ---
    let previousJwtHash = rootHash
    let previousPayload = rootPayload

    for (let i = 1; i < chain.length; i++) {
        let currentPayload
        try {
            currentPayload = jose.decodeJwt(chain[i])
        } catch {
            return { valid: false, error: `Token at depth ${i} is not a valid JWT` }
        }

        // Check parent link
        if (currentPayload.parent !== previousJwtHash) {
            return { valid: false, error: `Broken chain at depth ${i}: parent hash mismatch` }
        }

        // Check depth, relative to the root's: a retrofit root is a parentless
        // anchor at depth 1 whose children sit at position 1 with depth 2.
        // Mirrors localWebDAV/src/chains.mjs.
        const expectedDepth = (rootPayload.depth ?? 0) + i
        if (currentPayload.depth !== expectedDepth) {
            return { valid: false, error: `Depth mismatch at position ${i}: expected ${expectedDepth}, got ${currentPayload.depth}` }
        }

        const maxDepth = currentPayload.max_depth ?? DEFAULT_MAX_DEPTH
        if (currentPayload.depth >= maxDepth) {
            return { valid: false, error: `Chain exceeds max_depth at depth ${i}` }
        }

        // Check attenuation: scope must be subset of parent
        if (!currentPayload.scope) {
            return { valid: false, error: `Token at depth ${i} missing scope` }
        }

        if (!isScopeSubset(currentPayload.scope, parentScope)) {
            return { valid: false, error: `Scope escalation at depth ${i}: child scope exceeds parent` }
        }

        // Check expiry attenuation: child exp <= parent exp
        if (currentPayload.exp && previousPayload.exp && currentPayload.exp > previousPayload.exp) {
            return { valid: false, error: `Expiry escalation at depth ${i}: child outlives parent` }
        }

        // Verify signature — signer is the delegator (identified by previous token)
        let header
        try {
            header = jose.decodeProtectedHeader(chain[i])
        } catch {
            return { valid: false, error: `Invalid header at depth ${i}` }
        }

        // Look up the signer's public key
        const delegatorUserId = previousPayload.sub
        const signerKeyInfo = await resolvePublicKey(delegatorUserId, header.kid)
        if (!signerKeyInfo) {
            return { valid: false, error: `Signer key not found at depth ${i}: ${header.kid}` }
        }
        if (signerKeyInfo.status === 'revoked') {
            return { valid: false, error: `Signer key revoked at depth ${i}: ${header.kid}` }
        }

        let signerPublicKey
        try {
            signerPublicKey = await jose.importJWK(signerKeyInfo.publicKey, 'PS256')
        } catch {
            return { valid: false, error: `Invalid signer public key at depth ${i}` }
        }

        try {
            await jose.jwtVerify(chain[i], signerPublicKey)
        } catch (err) {
            return { valid: false, error: `Signature invalid at depth ${i}: ${err.message}` }
        }

        // Check revocation
        const currentHash = await hashToken(chain[i])
        if (await isRevoked(currentHash, revocationAudit)) {
            return { valid: false, error: `Token at depth ${i} revoked` }
        }

        // Advance
        previousJwtHash = currentHash
        previousPayload = currentPayload
        parentScope = currentPayload.scope
    }

    return {
        valid: true,
        leafPayload: previousPayload,
        effectiveScope: previousPayload.scope,
        chainLength: chain.length,
        rootSub: rootUserId,
    }
}

/**
 * Resolve and verify a chain in one call.
 * Fetches parent tokens from the server, then verifies the full chain.
 *
 * @param {string} authUrl - Base auth URL (e.g., 'https://server.com/auth')
 * @param {string} leafJwt - The leaf JWT string
 * @param {Object} options
 * @param {Function} options.resolvePublicKey - async (userId, kid) => { publicKey: JWK, status } | null
 * @param {Function} [options.isRevoked] - async (tokenHash) => boolean
 * @returns {Promise<{ valid: boolean, error?: string, leafPayload?, effectiveScope?, chainLength? }>}
 */
/**
 * Verify an anchor-model leaf against its already-verified anchor chain.
 * Mirrors the server (chains.mjs verifyLeafAgainstAnchor): the anchor chain
 * established the effective scope; the leaf hangs off the leaf anchor by its
 * `anchor` claim. Checks the anchor link, scope attenuation, the leaf signature
 * (resolved for the leaf anchor's `sub` + the leaf's header kid), and leaf
 * revocation. The leaf is a projection at the leaf anchor's depth.
 *
 * @param {string} leafJwt
 * @param {string[]} anchorChain - ordered [rootAnchor, ..., leafAnchor]
 * @param {Object} anchorResult - the verifyChain result for the anchor chain
 * @param {Object} options - { resolvePublicKey, isRevoked }
 */
export async function verifyLeafAgainstAnchor(leafJwt, anchorChain, anchorResult, options = {}) {
    const { resolvePublicKey, isRevoked = async () => false, revocationAudit } = options
    if (!resolvePublicKey) {
        return { valid: false, error: 'resolvePublicKey callback is required' }
    }

    let leafPayload
    try {
        leafPayload = jose.decodeJwt(leafJwt)
    } catch {
        return { valid: false, error: 'Leaf is not a valid JWT' }
    }

    const leafAnchor = anchorChain[anchorChain.length - 1]
    if (leafPayload.anchor !== await hashToken(leafAnchor)) {
        return { valid: false, error: 'Leaf anchor hash mismatch' }
    }

    if (!leafPayload.scope) {
        return { valid: false, error: 'Leaf missing scope' }
    }
    // The working `exp` is an enforced rule of the model, not a convention: an
    // anchor must have no exp, a leaf must have one (Phase 3.5, mirrors server).
    if (typeof leafPayload.exp !== 'number') {
        return { valid: false, error: 'Leaf missing exp — anchor-model leaves must be time-boxed' }
    }
    if (!isScopeSubset(leafPayload.scope, anchorResult.effectiveScope)) {
        return { valid: false, error: 'Leaf scope exceeds its anchor' }
    }

    const leafAnchorPayload = jose.decodeJwt(leafAnchor)
    let header
    try {
        header = jose.decodeProtectedHeader(leafJwt)
    } catch {
        return { valid: false, error: 'Invalid leaf header' }
    }

    const signerKeyInfo = await resolvePublicKey(leafAnchorPayload.sub, header.kid)
    if (!signerKeyInfo) {
        return { valid: false, error: `Leaf signer key not found: ${header.kid}` }
    }
    if (signerKeyInfo.status === 'revoked') {
        return { valid: false, error: `Leaf signer key revoked: ${header.kid}` }
    }

    let signerPublicKey
    try {
        signerPublicKey = await jose.importJWK(signerKeyInfo.publicKey, 'PS256')
    } catch {
        return { valid: false, error: 'Invalid leaf signer public key' }
    }

    try {
        await jose.jwtVerify(leafJwt, signerPublicKey)
    } catch (err) {
        return { valid: false, error: `Leaf signature invalid: ${err.message}` }
    }

    if (await isRevoked(await hashToken(leafJwt), revocationAudit)) {
        return { valid: false, error: 'Leaf revoked' }
    }

    return {
        valid: true,
        leafPayload,
        effectiveScope: leafPayload.scope,
        chainLength: anchorChain.length,
        rootSub: anchorResult.rootSub,
        model: 'anchor',
    }
}

export async function resolveAndVerify(authUrl, leafJwt, options = {}) {
    // An anchor is never presentable — refuse before resolving (Phase 3.5,
    // mirrors the server's entry-point gate), with the invariant's own name
    // rather than the resolve error it would hit below.
    if (isPresentedAnchor(leafJwt)) {
        return { valid: false, error: 'This token is an anchor (inert structural node) and cannot be used to authenticate' }
    }
    const { chain, leaf, model, error: resolveError } = await resolveChain(authUrl, leafJwt, { resolveLink: options.resolveLink })
    if (!chain) {
        return { valid: false, error: resolveError }
    }
    if (model !== 'anchor') {
        // Unreachable while resolveChain is anchor-only (dual-accept removed
        // 2026-10-06); kept so a future resolver arm cannot skip the leaf step.
        return { valid: false, error: 'Chains are anchor-model only' }
    }

    // One audit for the whole verification. A caller may pass its own to
    // aggregate across several, but by default every call gets a fresh record
    // so `revocationCheck` describes this credential and no other.
    const revocationAudit = options.revocationAudit || {}
    const opts = { ...options, revocationAudit }

    // allowAnchorChain: an anchor chain legitimately verifies here — the leaf
    // step below is what makes it a credential check.
    const result = await verifyChain(chain, { ...opts, allowAnchorChain: true })
    if (!result.valid) {
        return result
    }
    const verified = await verifyLeafAgainstAnchor(leaf, chain, result, opts)

    // Say which of the two reasons allowed this. `confirmed` means every
    // revocation source answered; `degraded` means at least one could not be
    // reached, so "not revoked" here means "not known to be revoked" and the
    // fail-open posture is what let it through. Both are `valid: true`; the
    // difference is what a caller is entitled to conclude, and until now the
    // two were the same boolean.
    if (verified.valid) {
        verified.revocationCheck = revocationAudit.unreachable ? 'degraded' : 'confirmed'
    }
    return verified
}

// --- Key Resolvers ---

/**
 * Create a public key resolver that fetches from the auth server.
 * Uses an in-memory cache to avoid repeated fetches for the same key.
 *
 * Endpoint: GET /auth/users/{userId}/keys/{kid}/public
 * Returns: { success, userId, kid, publicKey (JWK), status }
 *
 * @param {string} authUrl - Base auth URL (e.g., 'https://server.com/auth')
 * @returns {Function} async (userId, kid) => { publicKey: JWK, status: string } | null
 */
export function createServerKeyResolver(authUrl) {
    const cache = new Map()

    return async function resolvePublicKey(userId, kid) {
        const cacheKey = `${userId}:${kid}`
        if (cache.has(cacheKey)) return cache.get(cacheKey)

        try {
            const resp = await fetch(`${authUrl}/users/${userId}/keys/${kid}/public`)
            if (!resp.ok) return null

            const data = await resp.json()
            if (!data.success) return null

            const result = { publicKey: data.publicKey, status: data.status }
            cache.set(cacheKey, result)
            return result
        } catch {
            return null
        }
    }
}

/**
 * Create a public key resolver that checks the current user's local keys.
 * Only resolves keys belonging to the current Acequia user.
 * Useful for self-verification (owner checking their own tokens).
 *
 * @returns {Function} async (userId, kid) => { publicKey: JWK, status: string } | null
 */
export function createLocalKeyResolver() {
    return async function resolvePublicKey(userId, kid) {
        // This browser's user, now or before a device link (identities.js)
        const own = await ownIdentityFor(userId).catch(() => null)
        const keys = own?.userKeys
        if (!keys) return null

        const jwk = await jose.exportJWK(keys.publicKey)
        const thumbprint = await jose.calculateJwkThumbprint(jwk)
        if (thumbprint !== kid) return null

        return { publicKey: jwk, status: 'active' }
    }
}

/**
 * Create a key resolver for server-attested tokens (kid: "server-{domain}").
 * Fetches the domain's server signing public key via GET /auth/server-key.
 * Only resolves kids matching the "server-*" pattern.
 *
 * @param {string} authUrl - Base auth URL (e.g., 'https://server.com/auth')
 * @returns {Function} async (userId, kid) => { publicKey: JWK, status: string } | null
 */
// Persistent cache for server signing keys. Separate from the peer-token key
// store: different shape, different lifecycle, and only content-addressed
// entries are ever written here.
let serverKeyCacheStore
if (typeof window !== 'undefined' || typeof self !== 'undefined') {
    // Browser or Service Worker — matching walletStore above. Deliberately not
    // isBrowser(), which is browser-only: sw.js runs acequia too and needs this
    // cache as much as a page does.
    try {
        serverKeyCacheStore = localforage.createInstance({ name: 'acequia-server-keys' })
    } catch { /* non-browser context */ }
}

/**
 * Is this kid the content address of this key?
 *
 * The whole caching decision rests on this one question, and it is answerable
 * without knowing the domain, parsing the kid, or trusting the server: hash the
 * key and see whether it names itself. A `server-{thumbprint}` kid means one
 * key forever and is safe to hold; a `server-{domain}` role name means
 * "whoever signs now" and must never be persisted, or a future rotation would
 * have to reach into every browser's IndexedDB to take effect.
 */
async function kidNamesThisKey(kid, jwk) {
    if (!kid || !jwk) return false
    try {
        return kid === `server-${await jose.calculateJwkThumbprint(jwk)}`
    } catch {
        return false
    }
}

/**
 * Resolve the domain's server signing key by kid.
 *
 * Content-addressed kids are cached permanently (IndexedDB, surviving reload) —
 * this is what lets a server-rooted grant verify with the domain server
 * unreachable. Role-named kids are fetched every session and held in memory
 * only, deliberately: see kidNamesThisKey.
 */
export function createServerSigningKeyResolver(authUrl) {
    const memory = new Map()

    return async function resolvePublicKey(userId, kid) {
        if (!kid || !kid.startsWith('server-')) return null

        const hit = memory.get(kid)
        if (hit) return hit

        // Persistent cache — only ever holds content-addressed keys, and the
        // name is re-checked on read so a tampered entry is unusable.
        if (serverKeyCacheStore) {
            try {
                const stored = await serverKeyCacheStore.getItem(kid)
                if (stored?.publicKey && (await kidNamesThisKey(kid, stored.publicKey))) {
                    memory.set(kid, stored)
                    return stored
                }
            } catch { /* fall through to network */ }
        }

        let data = null
        try {
            // By-kid first: it answers for retired keys as well as the active
            // one, and the server resolves the role name through it too.
            let resp = await fetch(`${authUrl}/server-keys/${encodeURIComponent(kid)}`)
            if (!resp.ok) {
                // Older server without the by-kid route.
                resp = await fetch(`${authUrl}/server-key`)
                if (!resp.ok) return null
            }
            data = await resp.json()
        } catch {
            return null
        }
        if (!data?.success || !data.publicKey) return null

        const entry = { publicKey: data.publicKey, status: data.status || 'active' }
        memory.set(kid, entry)

        // Persist only if the answer names itself. A role-named key stays in
        // memory, so it is re-fetched next session and a rotation lands.
        if (serverKeyCacheStore && (await kidNamesThisKey(kid, entry.publicKey))) {
            try {
                await serverKeyCacheStore.setItem(kid, entry)
            } catch { /* best-effort */ }
        }
        return entry
    }
}

/**
 * Create a combined key resolver that tries local keys first,
 * then server signing keys (for server-attested tokens),
 * then falls back to the user key server endpoint.
 * This is the recommended resolver for most Acequia apps.
 *
 * @param {string} authUrl - Base auth URL
 * @returns {Function} async (userId, kid) => { publicKey: JWK, status: string } | null
 */
/**
 * A resolver over this account's cached device keys (cacheAccountKeys): the
 * user's other devices' keys, with no network. Only this account's; a
 * revoked key resolves as revoked.
 */
export function createAccountKeyResolver() {
    return async function resolvePublicKey(userId, kid) {
        if (!accountKeyStore || !kid) return null
        try {
            const row = await accountKeyStore.getItem(kid)
            // This account's key, or an alias of it (an identity one of its
            // browsers had before linking — the key is the alias's own)
            if (!row?.jwk || row.userId !== userId || (row.account || row.userId) !== getUserId()) return null
            // The kid is the key's thumbprint: a row filed under another
            // key's name is unusable, not believed
            if (await jose.calculateJwkThumbprint(row.jwk) !== kid) return null
            return { publicKey: row.jwk, status: row.status || 'active' }
        } catch {
            return null
        }
    }
}

/**
 * Fetch and keep this account's device public keys, while the auth server is
 * reachable and this browser is signed in: GET /users/:id/keys (self only),
 * then each key's public half. Afterwards a grant signed on any of the
 * account's devices verifies here offline (createCombinedKeyResolver reads
 * them before the network).
 * @returns {Promise<{ cached: number, skipped?: string }>}
 */
export async function cacheAccountKeys(authUrl = '/auth') {
    if (!accountKeyStore) return { cached: 0, skipped: 'no store' }
    await acequiaReady()
    const userId = getUserId()
    if (!userId) return { cached: 0, skipped: 'no user' }
    let list
    try {
        const resp = await fetch(`${authUrl}/users/${encodeURIComponent(userId)}/keys`)
        if (!resp.ok) return { cached: 0, skipped: `keys ${resp.status}` }
        list = (await resp.json()).keys || []
    } catch {
        return { cached: 0, skipped: 'offline' }
    }
    let cached = 0
    for (const key of list) {
        if (!key?.kid) continue
        try {
            const resp = await fetch(`${authUrl}/users/${encodeURIComponent(userId)}/keys/${encodeURIComponent(key.kid)}/public`)
            if (!resp.ok) continue
            const data = await resp.json()
            if (!data?.publicKey || await jose.calculateJwkThumbprint(data.publicKey) !== key.kid) continue
            await accountKeyStore.setItem(key.kid, { jwk: data.publicKey, userId, status: data.status || key.status || 'active', cachedAt: new Date().toISOString() })
            cached++
        } catch { /* one key fails; the others still count */ }
    }
    // The account's aliases: identities its browsers had before linking, each
    // with its own key (GET /users/:id/aliases, the account only)
    try {
        const resp = await fetch(`${authUrl}/users/${encodeURIComponent(userId)}/aliases`)
        const aliases = resp.ok ? (await resp.json()).aliases || [] : []
        for (const a of aliases) {
            if (!a?.kid || !a.publicKey || await jose.calculateJwkThumbprint(a.publicKey) !== a.kid) continue
            await accountKeyStore.setItem(a.kid, { jwk: a.publicKey, userId: a.userId, account: userId, status: a.status || 'active', cachedAt: new Date().toISOString() })
            cached++
        }
    } catch { /* aliases are extra; the account's own keys are kept */ }
    return { cached }
}

export function createCombinedKeyResolver(authUrl) {
    const localResolver = createLocalKeyResolver()
    const accountResolver = createAccountKeyResolver()
    const serverSigningResolver = createServerSigningKeyResolver(authUrl)
    const serverResolver = createServerKeyResolver(authUrl)

    return async function resolvePublicKey(userId, kid) {
        // Server-attested tokens (kid: "server-{domain}")
        if (kid && kid.startsWith('server-')) {
            return serverSigningResolver(userId, kid)
        }

        // Try local first (fast, no network)
        const local = await localResolver(userId, kid)
        if (local) return local

        // This account's other devices, cached (no network)
        const account = await accountResolver(userId, kid)
        if (account) return account

        // Fall back to user key endpoint on server
        return serverResolver(userId, kid)
    }
}

/**
 * The offline revocation posture, stated once, in the one place it applies.
 *
 * **A revocation source we cannot reach does not refuse the credential.**
 *
 * This is a decision (2026-08-24), not the residue of a `catch` block, and the
 * reasoning is the platform's: the domain server is a rendezvous, not the
 * source of truth. Failing closed would undo local-first verification — a peer
 * that can verify a whole chain offline would then refuse everything it
 * verified, which is worse than the exposure it prevents. The bound on a stale
 * grant is therefore leaf expiry, not propagation, and shortening that bound is
 * the lever that actually works.
 *
 * It applies domain-wide, with no scope-dependent split: a write does not get
 * a stricter offline answer than a read. Two postures would mean two failure
 * modes to reason about, for a distinction the credential itself already makes.
 *
 * Every caller records the fact, so "allowed, and revocation was checked" and
 * "allowed, but nothing could be asked" stay distinguishable downstream —
 * before this, both were the boolean `false`.
 *
 * To change the posture, change this function. There is nowhere else it lives.
 *
 * @param {Object} [audit] - per-verification record; annotated, never read here
 * @returns {boolean} always false — "not known to be revoked"
 */
export function revocationSourceUnreachable(audit) {
    if (audit) audit.unreachable = true
    return false
}

/**
 * Create a revocation checker that checks the local IndexedDB wallet.
 * Tokens marked as revoked in the wallet will fail verification.
 *
 * The wallet is local, so it is never unreachable — it has no offline mode to
 * report and never marks the audit.
 *
 * @returns {Function} async (tokenHash) => boolean
 */
export function createWalletRevocationChecker() {
    return async function isRevoked(tokenHash) {
        if (!walletStore) return false
        const entry = await walletStore.getItem(tokenHash)
        return entry?.status === 'revoked'
    }
}

/**
 * Server-backed revocation checker — what lets a PEER verifier see the
 * domain's revocation list instead of only its own wallet (the Q1 gap:
 * a grant revoked on the server kept working against peer origins).
 *
 * Reads the public membership check GET {authUrl}/revocations/check/:hash.
 * The domain list is append-only, so `revoked: true` is cached forever; a clean
 * answer is trusted briefly. Offline it reports through
 * `revocationSourceUnreachable`, which is where the posture is stated.
 */
export function createServerRevocationChecker(authUrl = '/auth') {
    const revokedForever = new Set()
    const cleanUntil = new Map() // hash -> epoch ms the clean answer expires

    // How long a "not revoked" answer is reused, and therefore the window in
    // which a fresh revocation still passes while fully online.
    //
    // This was 60s, inherited unexamined, and 60s is the wrong shape for the
    // job: the cache exists to stop a chain walk asking about the same hash
    // once per link, and a walk finishes in milliseconds. The rest of that
    // minute bought nothing and delayed every new revocation.
    //
    // 5s was chosen to collapse a walk's burst. It turned out not to be
    // nothing: viewers polling a camera every 10 s missed it every time, and
    // revocation lookups were 29% of acequia.io's requests (2026-10-20). Since
    // 2026-10-21 a clean answer lasts a minute (the owner's choice), as the
    // stored checker's does (revocations.js CLEAN_TTL_MS): a revocation made
    // elsewhere takes effect within a minute.
    const CLEAN_TTL_MS = 60 * 1000

    return async function isRevoked(tokenHash, audit) {
        if (!tokenHash) return false
        if (revokedForever.has(tokenHash)) return true
        const until = cleanUntil.get(tokenHash)
        if (until && Date.now() < until) return false
        let resp
        try {
            resp = await fetch(`${authUrl}/revocations/check/${encodeURIComponent(tokenHash)}`)
        } catch {
            return revocationSourceUnreachable(audit)
        }
        // A server that answers with an error is still a server we reached, but
        // it did not tell us this hash is clean — so it is not cached as clean,
        // and it counts as unreached rather than as a verdict.
        if (!resp.ok) return revocationSourceUnreachable(audit)
        let revoked
        try {
            ;({ revoked } = await resp.json())
        } catch {
            return revocationSourceUnreachable(audit)
        }
        if (revoked) {
            revokedForever.add(tokenHash)
            return true
        }
        cleanUntil.set(tokenHash, Date.now() + CLEAN_TTL_MS)
        return false
    }
}

/**
 * Compose revocation checkers: revoked if ANY says so. A checker that
 * throws never un-revokes what another one found.
 *
 * The audit is passed through to every checker, so one unreachable source marks
 * the whole verification degraded even when the others answered — which is the
 * honest reading. "Two of three sources said nothing" is not the same evidence
 * as "three of three said nothing".
 */
export function createCombinedRevocationChecker(...checkers) {
    return async function isRevoked(tokenHash, audit) {
        for (const check of checkers) {
            try {
                if (await check(tokenHash, audit)) return true
            } catch {
                // A checker that throws told us nothing — the same situation as
                // a source we could not reach, and recorded as such rather than
                // silently absorbed.
                revocationSourceUnreachable(audit)
            }
        }
        return false
    }
}

// --- IndexedDB Wallet CRUD ---

/**
 * List all token entries from the IndexedDB wallet.
 * @returns {Promise<Object[]>} Array of wallet entries
 */
export async function listWalletTokens({ appGrants = false } = {}) {
    if (!walletStore) return []

    // An app's peer grants (acequia.grants, kind 'app-grant') share the wallet
    // so the anchor walk and the revocation checker find them, but they are
    // an app's, bound to it by their audience: the dashboard's lists and the
    // TokenKeychain don't see them unless asked
    const tokens = []
    await walletStore.iterate((value) => {
        if (!appGrants && value?.kind === 'app-grant') return
        tokens.push(value)
    })

    // Sort by creation date, newest first
    tokens.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    return tokens
}

/**
 * Resolve a wallet row by any of its identities (Phase 5 re-key). Rows are
 * keyed by ANCHOR hash for anchor-model grants and by leaf hash for legacy
 * ones, but callers historically pass the leaf hash — and the current leaf
 * rotates on renewal. Direct key hit first, then a scan matching the row's
 * anchorHash or (current) leafHash.
 * @returns {Promise<{key: string, entry: Object}|null>}
 */
async function resolveWalletRow(hashOrKey) {
    if (!walletStore || !hashOrKey) return null
    const direct = await walletStore.getItem(hashOrKey)
    if (direct) return { key: hashOrKey, entry: direct }
    let found = null
    await walletStore.iterate((entry, key) => {
        if (entry && (entry.anchorHash === hashOrKey || entry.leafHash === hashOrKey)) {
            found = { key, entry }
            return found // non-undefined return stops localforage iteration
        }
    })
    return found
}

/**
 * Get a single wallet entry by anchor hash, leaf hash, or store key.
 * @returns {Promise<Object|null>}
 */
export async function getWalletToken(hashOrKey) {
    const row = await resolveWalletRow(hashOrKey)
    return row ? row.entry : null
}

/**
 * Update wallet entry metadata (name, note).
 * @param {string} hashOrKey - anchor hash, leaf hash, or store key
 * @param {Object} updates - Fields to update (name, note)
 * @returns {Promise<Object>} Updated entry
 */
export async function updateWalletToken(hashOrKey, updates) {
    if (!walletStore) throw new Error('Wallet not available')

    const row = await resolveWalletRow(hashOrKey)
    if (!row) throw new Error('Token not found in wallet')

    const updated = { ...row.entry, ...updates }
    await walletStore.setItem(row.key, updated)
    return updated
}

/**
 * Remove a wallet entry (after revocation or cleanup).
 * @param {string} hashOrKey - anchor hash, leaf hash, or store key
 */
export async function removeWalletToken(hashOrKey) {
    if (!walletStore) return
    const row = await resolveWalletRow(hashOrKey)
    if (row) await walletStore.removeItem(row.key)
}

/**
 * Mark a wallet entry as revoked (keeps it in wallet for display).
 * @param {string} hashOrKey - anchor hash, leaf hash, or store key
 * @returns {Promise<Object>} Updated entry
 */
export async function revokeWalletToken(hashOrKey) {
    if (!walletStore) throw new Error('Wallet not available')

    const row = await resolveWalletRow(hashOrKey)
    if (!row) throw new Error('Token not found in wallet')

    const updated = {
        ...row.entry,
        status: 'revoked',
        revokedAt: new Date().toISOString(),
    }
    await walletStore.setItem(row.key, updated)
    return updated
}